/**
 * Base Game Mode
 * 
 * Abstract base class that defines the interface for all game modes.
 * Each mode (Classic, Time Attack, Puzzle, etc.) extends this class
 * and implements mode-specific game logic.
 */

import { Grid } from '../Grid'
import { BaseMatchingEngine } from './BaseMatchingEngine'
import { FallingPair, PairGenerator } from '../FallingPair'
import { GameBalanceManager } from '../GameBalanceManager'
import { GameStats, GameConfig } from './GameCore'
import { ScreenshotMode } from '../../utils/ScreenshotMode'
import { CellType, CellUtils, CellState } from '../CellTypes'

/**
 * Game state enumeration (shared across all modes)
 */
export enum GameState {
    WAITING_TO_START = 'waiting_to_start',
    PLAYING = 'playing',
    FALLING = 'falling',
    MATCHING = 'matching',
    PAUSED = 'paused',
    GAME_OVER = 'game_over',
    VICTORY = 'victory'
}

/**
 * Mode-specific configuration interface
 */
export interface ModeConfig extends GameConfig {
    // Modes can extend this with additional config
    [key: string]: any
}

/**
 * Abstract base class for game modes
 */
export abstract class BaseGameMode {
    // Core game systems (shared across modes)
    protected grid: Grid
    protected matchingEngine: BaseMatchingEngine
    protected pairGenerator: PairGenerator
    protected balanceManager: GameBalanceManager

    // Current game state
    protected currentPair: FallingPair | null
    protected gameState: GameState
    protected stats: GameStats
    protected config: ModeConfig

    // Timing and pause management
    protected gameStartTime: number
    protected pausedTime: number
    protected totalPausedDuration: number

    constructor(
        grid: Grid,
        matchingEngine: BaseMatchingEngine,
        pairGenerator: PairGenerator,
        balanceManager: GameBalanceManager,
        config: ModeConfig
    ) {
        this.grid = grid
        this.matchingEngine = matchingEngine
        this.pairGenerator = pairGenerator
        this.balanceManager = balanceManager
        this.config = config

        this.currentPair = null
        this.gameState = GameState.WAITING_TO_START
        this.stats = this.createInitialStats()

        this.gameStartTime = 0
        this.pausedTime = 0
        this.totalPausedDuration = 0
    }

    // ========================================
    // Abstract Methods (Must Be Implemented)
    // ========================================

    /**
     * Initialize mode-specific systems
     * Called before game starts
     */
    public abstract initialize(): void

    /**
     * Update mode logic based on delta time
     * Each mode implements its own update loop
     */
    public abstract update(deltaTime: number): void

    /**
     * Handle player input actions
     * Return true if action was handled successfully
     */
    public abstract handleInput(action: string): boolean

    /**
     * Check if the game should end (mode-specific win/loss conditions)
     */
    protected abstract checkEndConditions(): void

    /**
     * Calculate score for cleared cells (mode-specific scoring)
     */
    protected abstract calculateScore(
        cellsCleared: number,
        chainLevel: number
    ): number

    // ========================================
    // Event System (Implemented by subclasses)
    // ========================================

    /**
     * Register an event listener
     */
    public abstract on(event: string, callback: Function): void

    /**
     * Emit an event to all registered listeners
     */
    protected abstract emit(event: string, ...args: any[]): void

    // ========================================
    // Common Methods (Can Be Overridden)
    // ========================================

    /**
     * Start the game
     */
    public startGame(): void {
        this.stats = this.createInitialStats()
        this.grid.clear()

        // Check if we should use a pre-filled grid for screenshots
        const usePrefilledGrid = ScreenshotMode.shouldUsePrefilledGrid()
        if (usePrefilledGrid) {
            console.log('📸 Using pre-filled grid for screenshot scenario')
            const prefilledGrid = ScreenshotMode.getPrefilledGrid()
            if (prefilledGrid) {
                this.loadPrefilledGrid(prefilledGrid)
            }
        }

        this.gameState = GameState.PLAYING
        this.gameStartTime = Date.now()
        this.totalPausedDuration = 0

        // Only initialize normal game loop if not using pre-filled grid
        // Pre-filled grids are static for screenshots - no spawning needed
        if (!usePrefilledGrid) {
            this.initialize()
        } else {
            console.log('📸 Skipping initialize() for pre-filled grid - static board for screenshot')
        }
    }

    /**
     * Load a pre-filled grid configuration (for screenshots)
     */
    private loadPrefilledGrid(gridData: Array<Array<number>>): void {
        console.log('📸 Loading pre-filled grid:', gridData.length, 'rows')

        // Map cell type numbers to CellType enum (using Puyo colors)
        const cellTypeMap: Record<number, CellType> = {
            1: CellType.RED,
            2: CellType.BLUE,
            3: CellType.GREEN,
            4: CellType.YELLOW,
            5: CellType.PURPLE,
            6: CellType.RED // Fallback to red for extra types
        }

        // Calculate offset to place cells at bottom of grid
        // Grid is 12 rows tall (0-11), so if we have 7 rows of data, start at row 5
        const gridHeight = 12
        const rowOffset = Math.max(0, gridHeight - gridData.length)
        console.log(`📸 Grid offset: ${rowOffset} (placing ${gridData.length} rows at bottom of ${gridHeight}-row grid)`)

        // Grid data is in [row][col] format, but Grid uses (x, y) = (col, row)
        for (let row = 0; row < gridData.length && row < 12; row++) {
            for (let col = 0; col < gridData[row].length && col < 6; col++) {
                const cellValue = gridData[row][col]
                if (cellValue !== 0 && cellTypeMap[cellValue]) {
                    const cellType = cellTypeMap[cellValue]
                    const y = row + rowOffset // Offset to place at bottom
                    const x = col // col index becomes x coordinate

                    // Create a new cell using CellUtils
                    const cell = CellUtils.createCell(cellType, x, y)

                    this.grid.placeCell(cell, x, y)
                }
            }
        }

        console.log('📸 Pre-filled grid loaded successfully')
    }

    /**
     * Pause the game
     */
    public pauseGame(): void {
        if (this.gameState === GameState.PLAYING || this.gameState === GameState.FALLING) {
            this.gameState = GameState.PAUSED
            this.pausedTime = Date.now()
        }
    }

    /**
     * Resume the game
     */
    public resumeGame(): void {
        if (this.gameState === GameState.PAUSED) {
            // Guard against invalid pausedTime
            if (this.pausedTime > 0) {
                const pauseDuration = Date.now() - this.pausedTime
                // Sanity check: ensure pause duration is reasonable (not negative, not absurdly large)
                if (pauseDuration >= 0 && pauseDuration < 7 * 24 * 60 * 60 * 1000) { // Max 7 days
                    this.totalPausedDuration += pauseDuration
                } else {
                    console.warn('BaseGameMode: Invalid pause duration detected, skipping accumulation')
                }
            }

            this.gameState = GameState.PLAYING

            // If there's no current pair, spawn one (e.g., after restore)
            if (!this.currentPair) {
                const nextConfig = this.pairGenerator.generateNext()
                this.currentPair = new FallingPair(nextConfig, this.grid)
                console.log('BaseGameMode: Spawned new pair after resume')
            }
        }
    }

    /**
     * Get current game state
     */
    public getState(): GameState {
        return this.gameState
    }

    /**
     * Get current game statistics
     */
    public getStats(): GameStats {
        return { ...this.stats }
    }

    /**
     * Get current configuration
     */
    public getConfig(): ModeConfig {
        return { ...this.config }
    }

    /**
     * Get the current falling pair
     */
    public getCurrentPair(): FallingPair | null {
        return this.currentPair
    }

    /**
     * Get the grid
     */
    public getGrid(): Grid {
        return this.grid
    }

    /**
     * Update elapsed time statistics
     */
    protected updateTimeStats(): void {
        if (this.gameState !== GameState.PAUSED) {
            const elapsed = Date.now() - this.gameStartTime - this.totalPausedDuration
            // Defensive guard: ensure time never goes negative
            this.stats.timeElapsed = Math.max(0, Math.floor(elapsed / 1000))
        }
    }

    /**
     * Create initial statistics object
     */
    protected createInitialStats(): GameStats {
        return {
            score: 0,
            level: 1,
            totalCellsCleared: 0,
            longestChain: 0,
            timeElapsed: 0,
            pairsDropped: 0
        }
    }

    /**
     * Restore game state from saved data
     */
    public restoreState(savedState: any): void {
        try {
            // Clear current state
            this.grid.clear()

            // Restore grid cells
            for (let y = 0; y < 12; y++) {
                for (let x = 0; x < 6; x++) {
                    const savedCell = savedState.grid[y][x]
                    if (savedCell) {
                        // Create new cell with saved data using CellUtils factory
                        const cell = CellUtils.createCell(savedCell.type as CellType, x, y)
                        cell.state = savedCell.state as CellState
                        cell.markedForDeletion = savedCell.markedForDeletion || false
                        this.grid.placeCell(cell, x, y)
                    }
                }
            }

            // Restore stats
            this.stats = { ...savedState.stats }

            // Restore timing information with validation
            const now = Date.now()

            // Validate gameStartTime - ensure it's not in the future or invalid
            if (!savedState.gameStartTime || savedState.gameStartTime <= 0 || savedState.gameStartTime > now) {
                console.warn('BaseGameMode: Invalid gameStartTime in save, using current time')
                this.gameStartTime = now
            } else {
                this.gameStartTime = savedState.gameStartTime
            }

            // Validate totalPausedDuration - ensure it's not negative or unreasonably large
            const maxPausedDuration = now - this.gameStartTime
            if (!savedState.totalPausedDuration || savedState.totalPausedDuration < 0 || savedState.totalPausedDuration > maxPausedDuration) {
                console.warn('BaseGameMode: Invalid totalPausedDuration in save, resetting to 0')
                this.totalPausedDuration = 0
            } else {
                this.totalPausedDuration = savedState.totalPausedDuration
            }

            // Set pausedTime to now since we're starting in paused state
            this.pausedTime = now

            // Set to paused state initially so player can resume when ready
            this.gameState = GameState.PAUSED

            // Clear current pair (will be respawned when game resumes)
            this.currentPair = null

            console.log('BaseGameMode: Game state restored successfully from', new Date(savedState.savedAt).toLocaleString())
        } catch (error) {
            console.error('BaseGameMode: Failed to restore game state:', error)
            // Fall back to starting a new game
            this.startGame()
        }
    }
}
